import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { ButtonComponent, ButtonGroupComponent, IconComponent } from '@pure/hive';
import {
    FeedbackState,
    IChatFeedbackRequest,
    IConversation,
    IConversationMessage,
    MessageFeedback,
} from '../model/conversation';
import {
    FeedbackStates,
    isFeedbackFailed,
    isFeedbackLoading,
    isFeedbackSuccessful,
    showMessageFeedback,
} from '../model/message-feedback-state';
import { ConversationPanelComponent } from '../conversation-panel/conversation-panel.component';
import { FollowUpChipComponent } from '../follow-up-chip/follow-up-chip.component';
import { DEFAULT_INPUT_PLACEHOLDER, InputBarComponent } from '../input-bar/input-bar.component';
import { TriageStatusComponent } from '../triage-status/triage-status.component';
import { MessageComponent } from '../message/message.component';
import {
    faArrowUpRightAndArrowDownLeftFromCenter,
    faChevronLeft,
    faClockRotateLeft,
    faPlus,
    faSparkles,
    faXmark,
} from '@fortawesome/pro-solid-svg-icons';

/**
 * Empty-state prompt shown when no conversation is active and no page summary is
 * displayed. Exported so the page-summary component can render the same text as its
 * idle/error fallback (see `[copilotEmptyState]` projection).
 */
export const COPILOT_EMPTY_PLACEHOLDER_TEXT = 'Ask AI Copilot anything about your fleet.';

/**
 * Panel-width variant of the triage prompt. Shorter than the full-page wording because the
 * side panel is narrow and the long form wraps to three lines here.
 */
export const TRIAGE_PANEL_PLACEHOLDER = 'Answer the question above';

@Component({
    selector: 'copilot-side-panel',
    standalone: true,
    imports: [
        ButtonComponent,
        ButtonGroupComponent,
        ConversationPanelComponent,
        FollowUpChipComponent,
        IconComponent,
        InputBarComponent,
        TriageStatusComponent,
        MessageComponent,
    ],
    templateUrl: './copilot-side-panel.component.html',
    styleUrl: './copilot-side-panel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        role: 'complementary',
        'aria-label': 'AI Copilot',
    },
})
export class CopilotSidePanelComponent implements OnChanges, OnDestroy {
    @Input({ transform: booleanAttribute }) isOpen: boolean = false;
    @Input() conversation!: IConversation | null;
    @Input() allConversations!: IConversation[];
    @Input({ transform: booleanAttribute }) waitingForInputResponse: boolean = false;
    @Input({ transform: booleanAttribute }) conversationLoading: boolean = false;
    /** Owned by CopilotChatController; a new reference on every transition drives this OnPush view. */
    @Input() feedbackStates: FeedbackStates = new Map();
    /**
     * True while the assistant is paused on a triage clarification. Drives the same
     * TRIAGE MODE indicator the full-page view shows — the panel is a second front door to
     * the same conversation, so a run paused in one must be visible and cancellable in
     * either.
     */
    @Input({ transform: booleanAttribute }) triageActive: boolean = false;

    @Output() submitMessage = new EventEmitter<string>();
    @Output() stopMessage = new EventEmitter<void>();
    @Output() regenerateResponse = new EventEmitter<void>();
    @Output() setVariation = new EventEmitter<{
        message: IConversationMessage;
        variation: number;
    }>();
    @Output() selectConversation = new EventEmitter<IConversation>();
    @Output() deleteConversation = new EventEmitter<IConversation>();
    @Output() submitFeedback = new EventEmitter<{
        feedback: MessageFeedback;
        message: IConversationMessage;
    }>();
    @Output() submitAdditionalFeedback = new EventEmitter<IChatFeedbackRequest>();
    @Output() closeAdditionalFeedback = new EventEmitter<{
        state: FeedbackState;
        message: IConversationMessage;
    }>();
    @Output() newChat = new EventEmitter<void>();
    @Output() expand = new EventEmitter<void>();
    @Output() closePanel = new EventEmitter<void>();
    @Output() exitTriage = new EventEmitter<void>();

    /** While paused, the composer answers the outstanding question rather than starting anew. */
    protected get inputPlaceholder(): string {
        return this.triageActive ? TRIAGE_PANEL_PLACEHOLDER : DEFAULT_INPUT_PLACEHOLDER;
    }

    @ViewChild(InputBarComponent) private readonly inputBar?: InputBarComponent;
    @ViewChild('messagesContainer')
    private readonly messagesContainer?: ElementRef<HTMLElement>;

    showHistory: boolean = false;

    private previouslyFocused: HTMLElement | null = null;
    private pendingScrollToBottom: boolean = false;
    private pendingFocusTimeoutId: number | null = null;
    private pendingScrollTimeoutId: number | null = null;

    @HostBinding('class.open') get hostOpen(): boolean {
        return this.isOpen;
    }

    @HostBinding('attr.aria-hidden') get hostAriaHidden(): string | null {
        return this.isOpen ? null : 'true';
    }

    @HostBinding('attr.inert') get hostInert(): string | null {
        return this.isOpen ? null : '';
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('isOpen' in changes) {
            // Cancel any focus that was scheduled by a previous open: on rapid
            // open/close it would otherwise fire after the panel is inert and
            // steal focus into a hidden region.
            this.cancelPendingFocus();
            if (this.isOpen) {
                this.previouslyFocused = document.activeElement as HTMLElement | null;
                this.pendingFocusTimeoutId = window.setTimeout(() => {
                    this.pendingFocusTimeoutId = null;
                    this.inputBar?.focus();
                });
            } else if (this.previouslyFocused) {
                this.previouslyFocused.focus();
                this.previouslyFocused = null;
            }
        }
        if ('feedbackStates' in changes && this.latestMessageFeedbackChanged(changes['feedbackStates'].previousValue)) {
            // The notification and additional-details form render below the last
            // message; in a panel this narrow they land under the fold otherwise.
            this.cancelPendingScroll();
            this.pendingScrollTimeoutId = window.setTimeout(() => {
                this.pendingScrollTimeoutId = null;
                this.scrollMessagesToBottom();
            });
        }
        if ('conversationLoading' in changes && this.pendingScrollToBottom && !this.conversationLoading) {
            this.pendingScrollToBottom = false;
            this.cancelPendingScroll();
            this.pendingScrollTimeoutId = window.setTimeout(() => {
                this.pendingScrollTimeoutId = null;
                this.scrollMessagesToBottom();
            });
        }
    }

    ngOnDestroy(): void {
        this.cancelPendingFocus();
        this.cancelPendingScroll();
    }

    @HostListener('keydown.escape')
    onEscape(): void {
        if (this.isOpen) {
            this.closePanel.emit();
        }
    }

    toggleHistory(): void {
        this.showHistory = !this.showHistory;
    }

    onBack(): void {
        this.showHistory = false;
    }

    onNewChat(): void {
        this.showHistory = false;
        this.newChat.emit();
    }

    onSelectConversation(convo: IConversation): void {
        this.pendingScrollToBottom = true;
        this.selectConversation.emit(convo);
        this.showHistory = false;
    }

    /**
     * Suggested follow-up questions to offer beneath the newest answer. Empty until the
     * response has finished streaming — the backend only appends its `<FOLLOWUPS>` block at
     * the end, so mid-stream chunks would otherwise pop chips in and out. Reads the variation
     * currently on screen rather than the newest one, so paging back through regenerated
     * responses shows that response's follow-ups.
     */
    /**
     * True until the conversation has its first message: the empty state is showing and whatever
     * the user types next starts the conversation. Both the empty state and the prompt-suggestion
     * dropdown hang off this, so they cannot drift apart — suggestions are for starting a
     * conversation, not for continuing one.
     */
    protected get isNewConversation(): boolean {
        return !this.conversation?.messages?.length;
    }

    protected get latestFollowUpQuestions(): string[] {
        if (this.waitingForInputResponse) {
            return [];
        }
        const latest = this.conversation?.messages?.at(-1);
        if (latest?.author !== 'ai') {
            return [];
        }
        return latest.variations[latest.selectedVariationIndex]?.followUpQuestions ?? [];
    }

    protected isFeedbackLoading(message: IConversationMessage): boolean {
        return isFeedbackLoading(this.feedbackStates, message);
    }

    protected isFeedbackSuccessful(message: IConversationMessage): boolean {
        return isFeedbackSuccessful(this.feedbackStates, message);
    }

    protected isFeedbackFailed(message: IConversationMessage): boolean {
        return isFeedbackFailed(this.feedbackStates, message);
    }

    protected showMessageFeedback(message: IConversationMessage): boolean {
        return showMessageFeedback(this.feedbackStates, message);
    }

    protected onCloseAdditionalFeedback(state: FeedbackState, message: IConversationMessage): void {
        this.closeAdditionalFeedback.emit({ state, message });
    }

    /** True when the feedback state of the conversation's newest message just changed. */
    private latestMessageFeedbackChanged(previous: FeedbackStates | undefined): boolean {
        const latest = this.conversation?.messages?.at(-1);
        const uuid = latest?.variations?.[latest.selectedVariationIndex]?.uuid;
        if (uuid == null) {
            return false;
        }
        return previous?.get(uuid) !== this.feedbackStates.get(uuid);
    }

    private scrollMessagesToBottom(): void {
        const el = this.messagesContainer?.nativeElement;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }

    private cancelPendingFocus(): void {
        if (this.pendingFocusTimeoutId !== null) {
            window.clearTimeout(this.pendingFocusTimeoutId);
            this.pendingFocusTimeoutId = null;
        }
    }

    private cancelPendingScroll(): void {
        if (this.pendingScrollTimeoutId !== null) {
            window.clearTimeout(this.pendingScrollTimeoutId);
            this.pendingScrollTimeoutId = null;
        }
    }

    protected readonly emptyPlaceholder = COPILOT_EMPTY_PLACEHOLDER_TEXT;
    protected readonly faChevronLeft = faChevronLeft;
    protected readonly faClockRotateLeft = faClockRotateLeft;
    protected readonly faExpand = faArrowUpRightAndArrowDownLeftFromCenter;
    protected readonly faPlus = faPlus;
    protected readonly faSparkles = faSparkles;
    protected readonly faXmark = faXmark;
}
